/**
 * The app-wide multi-tab lock `marg.lock` (architecture §4.1; exploration §5.3; ui-ux §3.4 E16): one tab plays, a second
 * tab shows «Открыто в другой вкладке» with [Играть здесь], which takes the lock over. A heartbeat `{ tabId, ts }` is
 * written every 4 s; a lock older than 12 s is stale. `BroadcastChannel('marg-lock')` makes the take-over instant.
 * In-memory (debug/capture) sessions never take the lock.
 */
import { STORAGE_KEYS } from '../shared';
import { newSaveSeed, type KeyValueStorage } from './save';

export const LOCK_HEARTBEAT_MS = 4000;
export const LOCK_STALE_MS = 12_000;

interface LockRecord { tabId: string; ts: number }

export class TabLock {
  readonly tabId: string;
  private timer: ReturnType<typeof setInterval> | null = null;
  private channel: BroadcastChannel | null = null;
  private held = false;
  /** Called when another tab takes the lock over (the app shows the notice and stops saving). */
  onLost: (() => void) | null = null;

  constructor(private readonly storage: KeyValueStorage, private readonly now: () => number = () => Date.now()) {
    // The id survives reloads of this tab (sessionStorage), so [Играть здесь] → reload does not lock itself out.
    let id: string | null = null;
    try { id = sessionStorage.getItem('marg.tabId'); } catch { id = null; }
    if (!id) {
      id = `${now().toString(36)}-${newSaveSeed()}`;
      try { sessionStorage.setItem('marg.tabId', id); } catch { /* private mode */ }
    }
    this.tabId = id;
    try {
      this.channel = new BroadcastChannel('marg-lock');
      this.channel.onmessage = (e: MessageEvent) => {
        const m = e.data as { kind?: string; tabId?: string };
        if (m?.kind === 'takeover' && m.tabId !== this.tabId && this.held) this.lose();
      };
    } catch { this.channel = null; }
  }

  private read(): LockRecord | null {
    const raw = this.storage.getItem(STORAGE_KEYS.lock);
    if (!raw) return null;
    try { const r = JSON.parse(raw) as LockRecord; return typeof r.tabId === 'string' && typeof r.ts === 'number' ? r : null; } catch { return null; }
  }

  /** True when another tab holds a fresh lock. */
  heldElsewhere(): boolean {
    const r = this.read();
    return r !== null && r.tabId !== this.tabId && this.now() - r.ts < LOCK_STALE_MS;
  }

  /** Takes the lock (force = [Играть здесь]); returns false when another tab holds it and force is false. */
  acquire(force = false): boolean {
    if (!force && this.heldElsewhere()) return false;
    this.held = true;
    this.beat(force);
    if (force) this.channel?.postMessage({ kind: 'takeover', tabId: this.tabId });
    if (this.timer === null) this.timer = setInterval(() => this.beat(), LOCK_HEARTBEAT_MS);
    return true;
  }

  get isHeld(): boolean { return this.held; }

  release(): void {
    if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
    if (this.held && this.read()?.tabId === this.tabId) this.storage.removeItem(STORAGE_KEYS.lock);
    this.held = false;
  }

  /** Writes the heartbeat; a newer record from another tab means it took over (unless `force`: our own take-over). */
  private beat(force = false): void {
    if (!this.held) return;
    const r = this.read();
    if (!force && r && r.tabId !== this.tabId && this.now() - r.ts < LOCK_STALE_MS && r.ts > this.lastBeat) { this.lose(); return; }
    this.lastBeat = this.now();
    try { this.storage.setItem(STORAGE_KEYS.lock, JSON.stringify({ tabId: this.tabId, ts: this.lastBeat })); } catch { /* ignore */ }
  }
  private lastBeat = 0;

  private lose(): void {
    if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
    this.held = false;
    this.onLost?.();
  }
}
